import React, { SetStateAction, useLayoutEffect, useMemo, useRef } from 'react';

import { getSongThemeColor } from '@/components/shared/SongThemeChip';
import { ResponsiveStream, StreamDatum } from '@nivo/stream';

import styles from './StreamChart.module.scss';

export type StreamChartData = StreamDatum[];

type StreamChartProps = {
	wrapperClassName?: string;
	data: StreamChartData;
	keys: string[];
	animate?: boolean;
	hoveredLabel?: string | null;
	onHoverChange?: React.Dispatch<SetStateAction<string | null>>;
	marginTop?: number;
	marginBottom?: number;
};

function hexToRgba(hex: string, alpha: number): string {
	const r = parseInt(hex.slice(1, 3), 16);
	const g = parseInt(hex.slice(3, 5), 16);
	const b = parseInt(hex.slice(5, 7), 16);
	return `rgba(${r},${g},${b},${alpha})`;
}

export const StreamChart: React.FC<StreamChartProps> = ({
	wrapperClassName,
	data,
	keys,
	animate,
	hoveredLabel,
	onHoverChange,
	marginTop = 25,
	marginBottom = 25,
}) => {
	const onHoverRef = useRef(onHoverChange);
	onHoverRef.current = onHoverChange;

	const totalsRef = useRef(new Map<string, number>());
	const totalSongsRef = useRef(0);

	const totals = useMemo(() => {
		const map = new Map<string, number>();
		for (const key of keys) {
			map.set(key, data.reduce((sum, point) => sum + (Number(point[key]) || 0), 0));
		}
		return map;
	}, [data, keys]);

	totalsRef.current = totals;
	totalSongsRef.current = data.length;

	const colors = useMemo(
		() =>
			keys.map((key) => {
				const hex = getSongThemeColor(key).colorHex;
				if (!hoveredLabel || hoveredLabel === key) return hex;
				return hexToRgba(hex, 0.15);
			}),
		[keys, hoveredLabel],
	);

	const gridXValues = Array.from({ length: data.length }, (_, i) => i);

	const LayerTooltip = useMemo(() => {
		return function StreamLayerTooltip({ layer }: { layer: { id: string | number } }) {
			useLayoutEffect(() => {
				const label = String(layer.id);
				onHoverRef.current?.(label);
				return () => {
					onHoverRef.current?.((prev) => prev === label ? null : prev);
				};
			}, [layer.id]);

			const label = String(layer.id);
			const value = totalsRef.current.get(label) ?? 0;
			const total = totalSongsRef.current;
			const pct = total > 0 ? ((value / total) * 100).toFixed(0) : '0';
			const displayLabel = label.replace(/\b\w/g, (c) => c.toUpperCase());

			return (
				<div style={{
					background: 'white',
					padding: '8px 12px',
					borderRadius: 4,
					boxShadow: '0 2px 8px rgba(0,0,0,.12)',
					lineHeight: 1.4,
					textAlign: 'center',
					fontSize: 12,
					whiteSpace: 'nowrap',
				}}>
					<div style={{ fontWeight: 500 }}>{displayLabel} · {pct}%</div>
				</div>
			);
		};
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<div className={`${styles['wrapper']} ${wrapperClassName ?? ''}`}>
			<ResponsiveStream
				data={data}
				keys={keys}
				margin={{ top: marginTop, right: 0, bottom: marginBottom, left: 0 }}
				axisTop={null}
				axisRight={null}
				axisBottom={null}
				axisLeft={null}
				enableGridX={true}
				gridXValues={gridXValues}
				enableGridY={false}
				offsetType="silhouette"
				curve="natural"
				colors={colors}
				fillOpacity={0.85}
				borderWidth={0}
				animate={animate || false}
				isInteractive={!!onHoverChange}
				enableStackTooltip={false}
				tooltip={onHoverChange ? LayerTooltip : undefined}
				theme={{
					grid: {
						line: {
							strokeDasharray: '0',
						},
					},
				}}
			/>
		</div>
	);
};
